import React from 'react';
import { StyleProp, TouchableOpacity, ViewStyle } from 'react-native';

import { Box } from '@components/Restyle';
import Icon from '@components/shared/Icon/Icon';
import { useHover } from '@hooks/useHover';
import Images from '@themes/images';

type Props = {
  onPress: () => void;
  accessibilityLabel: string;
  iconName?: keyof typeof Images;
  iconSize?: number;
  disabled?: boolean;
};

/**
 * Danger ghost icon button (TA-9005 — destructive icons are red on hover
 * only). At rest the icon is muted grey05 so the page doesn't alarm; on hover
 * the icon shifts to alertRed over a grey02 tint (one step deeper than the
 * grey01 row hover it usually sits on), giving a pre-click warning.
 * Callers should wrap it in a tooltip ("Remove" / "Delete") per the spec.
 */
export const DangerGhostIconButton = ({
  onPress,
  accessibilityLabel,
  iconName = 'Trash2',
  iconSize = 16,
  disabled = false,
}: Props) => {
  const { isHovered, hoverProps } = useHover({ disabled });
  return (
    <TouchableOpacity
      {...hoverProps}
      onPress={onPress}
      disabled={disabled}
      accessibilityLabel={accessibilityLabel}
      // `cursor` is a react-native-web extension not present in ViewStyle.
      style={
        {
          cursor: disabled ? 'not-allowed' : 'pointer',
        } as StyleProp<ViewStyle>
      }>
      <Box
        padding='xs'
        borderRadius='xs'
        alignItems='center'
        justifyContent='center'
        backgroundColor={isHovered ? 'grey02' : undefined}>
        <Icon
          name={iconName}
          width={iconSize}
          height={iconSize}
          color={isHovered ? 'alertRed' : 'grey05'}
        />
      </Box>
    </TouchableOpacity>
  );
};
